/**
 * Unlock state management in localStorage
 * Persists unlock status across sessions and page reloads
 */

const UNLOCK_KEY_PREFIX = 'prismatic_locked_content_';

export interface UnlockData {
  isUnlocked: boolean;
  email: string;
  unlockedAt: string; // ISO timestamp
  pageId: string; // unique identifier for the locked page
}

/**
 * Generate storage key for a specific page
 */
const getStorageKey = (pageId: string): string => `${UNLOCK_KEY_PREFIX}${pageId}`;

/**
 * Get unlock status for a specific page
 * Returns null if never unlocked
 */
export const getUnlockStatus = (pageId: string): UnlockData | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const key = getStorageKey(pageId);
    const stored = window.localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const data = JSON.parse(stored) as UnlockData;

    // Validate data structure
    if (typeof data === 'object' && data.isUnlocked && data.email && data.pageId) {
      return data;
    }

    return null;
  } catch (error) {
    console.error('Failed to get unlock status:', error);

    return null;
  }
};

/**
 * Save unlock status for a specific page
 */
export const saveUnlockStatus = (pageId: string, email: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const key = getStorageKey(pageId);
    const data: UnlockData = {
      isUnlocked: true,
      email,
      unlockedAt: new Date().toISOString(),
      pageId,
    };

    window.localStorage.setItem(key, JSON.stringify(data));

    return true;
  } catch (error) {
    console.error('Failed to save unlock status:', error);

    return false;
  }
};

/**
 * Clear unlock status for a specific page
 * Useful for testing or reset functionality
 */
export const clearUnlockStatus = (pageId: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const key = getStorageKey(pageId);
    window.localStorage.removeItem(key);

    return true;
  } catch (error) {
    console.error('Failed to clear unlock status:', error);

    return false;
  }
};

/**
 * Check if a page is currently unlocked
 * Quick boolean check without returning full data
 */
export const isPageUnlocked = (pageId: string): boolean => {
  const data = getUnlockStatus(pageId);

  return data?.isUnlocked ?? false;
};

/**
 * Get email used for unlock (if unlocked)
 */
export const getUnlockEmail = (pageId: string): string | null => {
  const data = getUnlockStatus(pageId);

  return data?.email ?? null;
};
